import { Text, Button } from '@janeapp/burrito-design-system';
import { LoadingView } from '~/components';
import { PractitionersSearchResult } from '~/api';
import { buildPractitionerBookingUrlWithTreatmentId } from '~/utilities';
import { analytics, trackEvent, useAnalytics } from '~/hooks';
import { Opening } from './Opening';
import { usePractitionerOpenings } from '~/queries/usePractitionerOpenings';

interface OpeningsProps {
  result: PractitionersSearchResult;
}

export const Openings = ({ result }: OpeningsProps) => {
  useAnalytics();
  const rudderstackSessionId = analytics?.getSessionId()?.toString() || '';

  // Extract locationId from marketplacePractitionerLocationId (format: "xxx-xx_ID")
  const locationId = Number(
    result.marketplacePractitionerLocationId.split('_').pop()
  );

  const { openings, isLoading, isError } = usePractitionerOpenings(
    result.staffMemberGuid,
    locationId
  );

  const handleOpeningClick = () => {
    if (!result.clinicBookingUrl) return;
    if (!result.defaultTreatmentId) return;

    trackEvent('mpl_view_booking_site_button_clicked', {
      name: result.fullName,
      clinic_name: result.clinicName,
      url: result.clinicBookingUrl,
    });

    const bookingUrl = buildPractitionerBookingUrlWithTreatmentId(
      result.staffMemberGuid,
      result.clinicBookingUrl,
      result.defaultTreatmentId,
      rudderstackSessionId
    );

    window.open(bookingUrl, '_blank');
  };

  const upcomingOpenings = openings.slice(0, 5);

  if (isLoading) {
    return (
      <div className="mt-4 flex items-center justify-center">
        <LoadingView loading={true} message={''} size="2xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4">
        <Text size="sm" className="italic text-neutral-60">
          Failed to load availability
        </Text>
      </div>
    );
  }

  if (upcomingOpenings.length === 0) {
    return (
      <div className="mt-4">
        <Text size="md" className="text-bold-90 mb-2 font-bold">
          Next available times with {result.firstName}
        </Text>
        <div className="rounded-md border border-neutral-30 bg-neutral-10 p-4 text-center">
          <Text size="sm" className="text-neutral-60">
            This practitioner doesn&apos;t have any availability in the next 7
            days.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Text size="md" className="text-bold-90 mb-2 font-bold">
        Next available times with {result.firstName}
      </Text>
      <div className="flex flex-col gap-2.5">
        {upcomingOpenings.map((opening) => (
          <Opening
            key={`${opening.start}-${opening.end}`}
            opening={opening}
            onBookingClick={handleOpeningClick}
          />
        ))}
      </div>
      <Button className="mt-2 w-full text-md" onClick={handleOpeningClick}>
        See more times
      </Button>
    </div>
  );
};
